import { useCallback, useEffect } from 'react';

/**
 * Single-source-of-truth helper za sync React state ↔ URL search param.
 * Koristi `window.history.replaceState` (ne mijenja history stack).
 *
 * Tipično koristenje:
 *   const [tab, setTab] = useState<string>(readUrlParam('tab', 'overview'));
 *   useUrlSync('tab', tab, tab === 'overview');
 *
 * `removeWhen` (true) briše param iz URL-a (npr. kad je default).
 */

export function readUrlParam(key: string, fallback: string): string {
    if (typeof window === 'undefined') return fallback;
    const v = new URLSearchParams(window.location.search).get(key);
    return v ?? fallback;
}

export function readUrlParamNum(key: string): number | null {
    if (typeof window === 'undefined') return null;
    const v = new URLSearchParams(window.location.search).get(key);
    return v && /^\d+$/.test(v) ? Number(v) : null;
}

export function useUrlSync(key: string, value: string | null | undefined, removeWhen: boolean = false): void {
    useEffect(() => {
        if (typeof window === 'undefined') return;
        const url = new URL(window.location.href);
        const current = url.searchParams.get(key);
        const shouldRemove = removeWhen || value == null || value === '';
        if (shouldRemove) {
            if (current !== null) {
                url.searchParams.delete(key);
                window.history.replaceState({}, '', url.toString());
            }
        } else if (current !== String(value)) {
            url.searchParams.set(key, String(value));
            window.history.replaceState({}, '', url.toString());
        }
    }, [key, value, removeWhen]);
}

/** Copy current URL to clipboard + show fallback prompt if no Clipboard API. */
export function copyCurrentUrl(noteHr?: string, noteEn?: string, locale: 'hr' | 'en' = 'hr'): void {
    if (typeof window === 'undefined') return;
    const url = window.location.href;
    const note = locale === 'en' ? (noteEn ?? 'Link copied:') : (noteHr ?? 'Link kopiran:');
    if (navigator.clipboard) {
        navigator.clipboard.writeText(url)
            .then(() => alert(`${note}\n${url}`))
            .catch(() => window.prompt(note, url));
    } else {
        window.prompt(note, url);
    }
}

/** Compact "🔗 Kopiraj link" gumb — koristi se u module hero-ima. */
import React from 'react';
export function CopyLinkButton({ locale, label }: { locale: 'hr' | 'en'; label?: string }) {
    return (
        <button
            type="button"
            onClick={() => copyCurrentUrl(undefined, undefined, locale)}
            title={locale === 'en' ? 'Copy deep link to current view' : 'Kopiraj deep link na trenutni prikaz'}
            style={{
                background: 'transparent',
                color: 'var(--ad-ink)',
                border: '1px solid rgba(var(--ad-soft-rgb),0.4)',
                padding: '6px 12px',
                borderRadius: 8,
                cursor: 'pointer',
                fontSize: '0.82rem',
                fontWeight: 600,
            }}
        >
            🔗 {label ?? (locale === 'en' ? 'Copy link' : 'Kopiraj link')}
        </button>
    );
}

/**
 * Oldschool progress overlay — terminal-style s ASCII spinner-om.
 * Koristi se za long-running operacije (PDF gen, file upload, import).
 *
 * Primjer:
 *   const [overlay, setOverlay] = useState<ProgressOverlayState | null>(null);
 *   setOverlay({ label: 'Importiranje 1/5: file.xlsx', progress: 20, phase: 'downloading', message: 'Parsiram redove…' });
 *   ...
 *   <ProgressOverlay state={overlay} />
 */
export type ProgressOverlayState = {
    label: string;
    progress: number;  // 0..100
    phase: 'preparing' | 'downloading' | 'done' | 'error';
    message?: string;
};

export function ProgressOverlay({ state }: { state: ProgressOverlayState | null }) {
    if (!state) return null;
    return (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#1a1a1a', color: 'var(--ad-acc)', fontFamily: 'DejaVu Sans Mono, Courier New, monospace', padding: '24px 32px', borderRadius: 8, border: '2px solid var(--ad-acc)', minWidth: 420, boxShadow: '0 8px 32px rgba(0,0,0,0.5)' }}>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, marginBottom: 12, letterSpacing: 0.5 }}>
                    {state.phase === 'error' ? '✗ FAIL' : state.phase === 'done' ? '✓ OK' : '⟳'} · {state.label}
                </div>
                <div style={{ background: '#000', height: 22, border: '1px solid var(--ad-acc)', position: 'relative', overflow: 'hidden' }}>
                    <div style={{
                        background: state.phase === 'error' ? '#b14040' : state.phase === 'done' ? '#1f7a36' : 'linear-gradient(90deg, var(--ad-acc-3), var(--ad-acc))',
                        height: '100%',
                        width: `${state.progress}%`,
                        transition: 'width 0.2s linear',
                    }} />
                    <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '0.78rem', color: '#fff', textShadow: '0 1px 2px rgba(0,0,0,0.8)', mixBlendMode: 'difference' }}>
                        {state.progress}%
                    </div>
                </div>
                <div style={{ marginTop: 10, fontSize: '0.78rem', color: '#e4d9b8' }}>
                    {state.phase !== 'done' && state.phase !== 'error' ? (
                        <span style={{ marginRight: 8 }}>{'['.padEnd(Math.floor(state.progress / 5) + 1, '#').padEnd(21, ' ')+']'}</span>
                    ) : null}
                    {state.message}
                </div>
            </div>
        </div>
    );
}
